import { T } from '@transifex/react'

import { pathJoin } from 'src/reducers/routing/utils'

const createContactDetailRoutesMap = (prefix: string) => ({
  overview: pathJoin(prefix)('overview').chain,
  // The composting register scoped to this individual (they hold the
  // notification; the composter may cover several properties).
  'composting-registries': pathJoin(prefix)('composting-registries').chain,
  // PRO-552 PR 3: the Activity tab (aggregated contact.eventLog feed).
  activity: pathJoin(prefix)('activity').chain,
  tickets: pathJoin(prefix)('tickets').chain,
  keys: pathJoin(prefix)('keys').chain,
  // PRODUCT-454: the customer's Rentals tab (Inventory module on).
  rentals: pathJoin(prefix)('rentals').chain,
  // PRO-552 PR 3: these were their own tabs (8→4). They are no longer in the
  // tab set — their content folds into the Overview cards + rail — but the
  // paths stay so their old bookmarks resolve (TabsNavigationExtended then
  // redirects an unknown tab key to Overview) and the rail's relation rows
  // keep a valid target.
  properties: pathJoin(prefix)('properties').chain,
  companies: pathJoin(prefix)('companies').chain,
  authorizations: pathJoin(prefix)('authorizations').chain,
  notes: pathJoin(prefix)('notes').chain,
  'scheduled-changes': pathJoin(prefix)('scheduled-changes').chain,
})

const createCompanyDetailRoutesMap = (prefix: string) => ({
  overview: pathJoin(prefix)('overview').chain,
  // PRO-578: the Activity tab (aggregated PMC eventLog feed).
  activity: pathJoin(prefix)('activity').chain,
  tickets: pathJoin(prefix)('tickets').chain,
  keys: pathJoin(prefix)('keys').chain,
  // PRODUCT-454: the customer's Rentals tab (Inventory module on).
  rentals: pathJoin(prefix)('rentals').chain,
  // PRO-578: Properties/Individuals fold into the Overview cards (5→4 tabs) —
  // the paths stay so old bookmarks resolve (TabsNavigationExtended then
  // redirects an unknown tab key to Overview) and the rail rows keep a valid
  // target.
  properties: pathJoin(prefix)('properties').chain,
  contacts: pathJoin(prefix)('contacts').chain,
})

export const createStandaloneContactsRouteMap = (prefix: string) => ({
  // The two registries carry their own titles so the breadcrumb on a list page
  // names that list. Without them `getRouteTitle` falls through to the
  // section's title, which is how the Individuals list used to read
  // "All Contacts" (PRO-24 naming leak).
  contacts: pathJoin(prefix)('contacts').chainWithTitle({
    title: <T _str="All Individuals" />,
    breadCrumbTitle: <T _str="All Individuals" />,
  }),
  companies: pathJoin(prefix)('companies').chainWithTitle({
    title: <T _str="All Companies" />,
    breadCrumbTitle: <T _str="All Companies" />,
  }),
  create: pathJoin(prefix)('create').chainWithTitle({
    title: <T _str="Create Individual" />,
    breadCrumbTitle: <T _str="Create Individual" />,
  }),
  'create-company': pathJoin(prefix)('create-company').chainWithTitle({
    title: <T _str="Create Company" />,
    breadCrumbTitle: <T _str="Create Company" />,
  }),
  '/contact/:id': (id: string) =>
    pathJoin(prefix)('contact')(id).chainMapAuto(createContactDetailRoutesMap, {
      title: <T _str="Individual Details" />,
      breadCrumbTitle: <T _str="Individual Details" />,
    }),
  '/company/:id': (id: string) =>
    pathJoin(prefix)('company')(id).chainMapAuto(createCompanyDetailRoutesMap, {
      title: <T _str="Company Details" />,
      breadCrumbTitle: <T _str="Company Details" />,
    }),
})
